import { useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Plus, Search, X as XIcon } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface SingleProps {
  multi?: false;
  options: string[];
  value: string;
  onChange: (next: string) => void;
  /** Called when the operator creates a new value not in `options`. */
  onCreate?: (created: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

interface MultiProps {
  multi: true;
  options: string[];
  value: string[];
  onChange: (next: string[]) => void;
  onCreate?: (created: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

type Props = SingleProps | MultiProps;

/**
 * Searchable picker for category-driven product attributes.
 *
 * One component, two modes:
 *   - `multi: false` (default): single-pick. Clicking a row selects it
 *     and closes the popover. Trigger shows the picked value.
 *   - `multi: true`: any number of values can be on. Trigger shows
 *     comma-joined values, or "N selected" once it gets long. The
 *     popover stays open so the operator can pick several in one go.
 *
 * Both modes support inline value creation: when the search text doesn't
 * match any existing option, a `+ Create "XYZ"` row appears at the
 * bottom. Picking it selects the new value and fires `onCreate` so the
 * parent can persist it back to the category template (auto-learn).
 *
 * Built on Radix Popover + Input — same primitives as the rest of the
 * design system, no extra deps.
 */
export function AttributePicker(props: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // Normalised view of selected values regardless of mode.
  const selectedArr: string[] = props.multi
    ? (props.value ?? [])
    : props.value
      ? [props.value]
      : [];
  const selectedSet = useMemo(
    () => new Set(selectedArr.map((v) => v.toLowerCase())),
    [selectedArr],
  );

  // Visible options = template options ∪ any selected values that aren't
  // in the template yet (e.g. previously-created custom values, so the
  // operator can see and de-select them).
  const allOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const o of props.options) {
      const k = o.toLowerCase();
      if (!seen.has(k)) {
        seen.add(k);
        out.push(o);
      }
    }
    for (const v of selectedArr) {
      const k = v.toLowerCase();
      if (!seen.has(k)) {
        seen.add(k);
        out.push(v);
      }
    }
    return out;
  }, [props.options, selectedArr]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allOptions;
    return allOptions.filter((o) => o.toLowerCase().includes(q));
  }, [allOptions, query]);

  const exactMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return allOptions.some((o) => o.toLowerCase() === q);
  }, [allOptions, query]);

  const canCreate = !!props.onCreate && query.trim().length > 0 && !exactMatch;

  const summary = (() => {
    if (selectedArr.length === 0) return props.placeholder ?? 'Select…';
    if (selectedArr.length <= 2) return selectedArr.join(', ');
    return `${selectedArr[0]}, ${selectedArr[1]} +${selectedArr.length - 2} more`;
  })();

  const pickValue = (v: string) => {
    if (props.multi) {
      const isOn = selectedSet.has(v.toLowerCase());
      const next = isOn
        ? props.value.filter((x) => x.toLowerCase() !== v.toLowerCase())
        : [...props.value, v];
      props.onChange(next);
    } else {
      props.onChange(v);
      setOpen(false);
    }
  };

  const handleCreate = () => {
    const raw = query.trim();
    if (!raw) return;
    // Capitalise first letter sensibly to keep the dropdown tidy
    // (operators tend to type lowercase; "cotton" → "Cotton").
    const created = raw[0].toUpperCase() + raw.slice(1);
    props.onCreate?.(created);
    pickValue(created);
    setQuery('');
    // Re-focus the search so a follow-up create flows.
    requestAnimationFrame(() => searchRef.current?.focus());
  };

  const clearAll = () => {
    if (props.multi) props.onChange([]);
    else props.onChange('');
  };

  return (
    <div className={cn('space-y-1', props.className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={props.disabled}
            aria-label={props.ariaLabel}
            className="h-9 w-full justify-between font-normal"
          >
            <span
              className={cn(
                'truncate',
                selectedArr.length === 0 && 'text-muted-foreground',
              )}
            >
              {summary}
            </span>
            <ChevronDown className="ml-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
        >
          <div className="border-b border-border/60 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canCreate) {
                    e.preventDefault();
                    handleCreate();
                  }
                }}
                placeholder="Search or type a new value…"
                className="h-8 pl-8"
                autoFocus
              />
            </div>
          </div>
          <ScrollArea className="max-h-[260px]">
            {filtered.length === 0 && !canCreate ? (
              <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">
                No matches.
              </div>
            ) : (
              <div className="py-1">
                {filtered.map((o) => {
                  const isSelected = selectedSet.has(o.toLowerCase());
                  return (
                    <button
                      key={o}
                      type="button"
                      onClick={() => pickValue(o)}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] hover:bg-muted/40',
                        isSelected && 'bg-primary/5',
                      )}
                    >
                      {props.multi ? (
                        <span
                          className={cn(
                            'grid h-4 w-4 shrink-0 place-items-center rounded border',
                            isSelected
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border',
                          )}
                        >
                          {isSelected && <Check className="h-3 w-3" />}
                        </span>
                      ) : (
                        <span className="grid h-4 w-4 shrink-0 place-items-center">
                          {isSelected && <Check className="h-3 w-3 text-primary" />}
                        </span>
                      )}
                      <span className="flex-1 truncate text-foreground">{o}</span>
                    </button>
                  );
                })}
                {canCreate && (
                  <button
                    type="button"
                    onClick={handleCreate}
                    className="flex w-full items-center gap-2 border-t border-border/60 px-3 py-1.5 text-left text-[12.5px] text-primary hover:bg-primary/5"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span className="flex-1 truncate">
                      Create <span className="font-semibold">"{query.trim()}"</span>
                    </span>
                  </button>
                )}
              </div>
            )}
          </ScrollArea>
          {selectedArr.length > 0 && (
            <div className="flex items-center justify-between border-t border-border/60 px-2 py-1.5">
              <span className="text-[11px] text-muted-foreground">
                {props.multi
                  ? `${selectedArr.length} selected`
                  : selectedArr[0]}
              </span>
              {!props.required && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-[11px] text-muted-foreground hover:text-destructive"
                  onClick={clearAll}
                >
                  <XIcon className="mr-1 h-3 w-3" />
                  Clear
                </Button>
              )}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
